import { action } from '@ember/object';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { LEGACY_DATASETS_TOOLTIP } from 'webapp/enums/metric-mover';
import { TourId } from 'webapp/enums/tour';

import CommonCollapsiblePillOptionsComponent from 'webapp/components/common/collapsible-pill-options';
import CustomMetricsEditActionsBaseComponent, {
	MAX_OPTIONS_SHOWN_ON_PAGE,
} from 'webapp/components/custom-metrics/edit/actions/base';
import type {
	ActionListItem,
	BaseAction,
	Args as BaseArgs,
} from 'webapp/components/custom-metrics/edit/actions/base';

import type CustomMetricSetupFormApiMetricBuilder from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder';
import type CustomMetricSetupFormDataset from 'webapp/models-custom/custom-metric-setup-form/dataset';
import type CustomMetricSetupFormPrebuilt from 'webapp/models-custom/custom-metric-setup-form/prebuilt';
import CustomMetricSetupFormSheet from 'webapp/models-custom/custom-metric-setup-form/sheet';

const enum Action {
	QUERY_WIZARD = 'openWizardFlow',
	SHEETS_VIDEO = 'openSheetsVideo',
	SHEETS_RELOAD = 'reloadSheetData',
	SHEETS_LINK = 'openSheetsLink',
}

interface Args extends BaseArgs {
	form:
		| CustomMetricSetupFormPrebuilt
		| CustomMetricSetupFormSheet
		| CustomMetricSetupFormApiMetricBuilder
		| CustomMetricSetupFormDataset;
	isViewOnly?: boolean;
	openWizardFlow?: () => void;
}

export default class CustomMetricsEditActionsCustomQueryComponent extends CustomMetricsEditActionsBaseComponent<Args> {
	@service('tour') declare tourService: Service.Tour;
	@service('navigation') declare navigationService: Service.Navigation;

	origin = 'metric_builder';

	videoGuideAction: ActionListItem<Action> = {
		icon: 'Video',
		text: 'Video guide',
		action: Action.SHEETS_VIDEO,
	};

	refreshSheetAction: ActionListItem<Action> = {
		icon: 'Refresh',
		text: 'Refresh sheet data',
		action: Action.SHEETS_RELOAD,
	};

	get linkSheetAction(): ActionListItem<Action> {
		return {
			icon: 'LinkedFile',
			text: 'Open original document',
			action: Action.SHEETS_LINK,
			href: (this.args.form as CustomMetricSetupFormSheet).fileUrl,
		};
	}

	get queryWizardAction(): ActionListItem<Action> {
		return {
			icon: 'Wizard',
			text: 'Switch to wizard',
			action: Action.QUERY_WIZARD,
			isDisabled: !this.isWizardFlowSupported,
			disabledTooltipText: this.isWizardFlowSupported
				? ''
				: this.unsupportedWizardFlowTooltipText,
		};
	}

	get query() {
		return this.args.form.query;
	}

	get showDuplicate() {
		return !this.query.isNew;
	}

	get isDuplicateBlocked() {
		return !!this.query.sourceObj?.isLegacyCreationBlocked;
	}

	get showDelete() {
		return !this.query.isNew && document.body.id !== 'wizard' && !this.args.isViewOnly;
	}

	get isSheets() {
		return this.query.isSheets && !this.query.isTypeOfDataset;
	}

	get showRefreshAndDocumentLinkActions() {
		if (!this.isSheets) {
			return false;
		}
		const source = this.args.form.query.sourceObj;
		return !source?.sourceType?.isFileUpload;
	}

	@cached
	get options() {
		const options = [];
		if (!!this.args.openWizardFlow) {
			options.push(this.queryWizardAction);
		}

		if (this.isSheets && !this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins) {
			options.push(this.videoGuideAction);
		}

		if (this.showGetAssistance) {
			options.push(this.getAssistanceAction);
		}

		if (this.showRefreshAndDocumentLinkActions) {
			options.push(this.refreshSheetAction);
			options.push(this.linkSheetAction);
		}

		if (this.showDuplicate) {
			options.push({
				...this.duplicateAction,
				isDisabled: this.isDuplicateBlocked,
				disabledTooltipText: this.isDuplicateBlocked ? LEGACY_DATASETS_TOOLTIP : undefined,
			});
		}

		if (this.showDelete) {
			options.push(this.deleteAction);
		}

		return options;
	}

	get isWizardFlowSupported(): boolean {
		return this.args.form.isWizardFlowSupported;
	}

	get unsupportedWizardFlowTooltipText(): string {
		let tooltipText = '<strong>Wizard mode not available</strong>';

		if (this.isSheets) {
			tooltipText +=
				'<p>The spreadsheet structure is not compatible to be used with the wizard.</p>';

			if (!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins) {
				tooltipText +=
					'<a href="https://help.databox.com/format-your-google-sheets-and-excel-files" target="_blank">Learn more</a>';
			}
		}

		return tooltipText;
	}

	get sourceTypes() {
		const sourceTypeKey = this.args.form.query.sourceObj?.sourceType?.key;
		if (sourceTypeKey) {
			return [sourceTypeKey];
		}
		return [];
	}

	@action
	override doAction(actionName?: string) {
		if (actionName) {
			this[actionName as BaseAction | Action]();
		}
	}

	@action
	openWizardFlow() {
		if (this.isWizardFlowSupported && this.args.openWizardFlow) {
			this.args.openWizardFlow();
		}
	}

	@action
	reloadSheetData() {
		const form = this.args.form;
		if (!(form instanceof CustomMetricSetupFormSheet)) {
			return;
		}
		if (!form.isSheetDataReloading) {
			form.reloadData();
		}
	}

	@action
	openSheetsVideo() {
		this.navigationService.openGoogleSheetsVideoPopup(
			this.args.form.query.trackingWizardType as 'sheets_wizard',
		);

		if (this.tourService.isEligibleForQbSheetsVideoTour) {
			const tour = this.tourService.initTour(TourId.QB_SHEETS_VIDEO);
			tour?.markDone();
		}
	}

	@action
	openSheetsLink() {
		const url = (this.args.form as CustomMetricSetupFormSheet).fileUrl;
		window.open(url, '_blank');
	}

	<template>
		<CommonCollapsiblePillOptionsComponent
			@itemsList={{this.options}}
			@maxVisibleItems={{MAX_OPTIONS_SHOWN_ON_PAGE}}
			@doAction={{this.doAction}}
		/>
	</template>
}
